import { Formik } from "formik";
import React from "react";
import { type ValidationError, string } from "yup";

import { type QueryView, Type } from "~/models/dto";

import { queryQueryNodesList } from "../../../queries";
import { useQuery } from "../../../queries/react-query";
import { Form } from "../../react/form/Form";
import { Input } from "../../react/form/Input";
import { TextArea } from "../../react/form/TextArea";
import { HelpPopupTooltip, HelpPopupTooltipMoreInfoLink } from "../../react/tooltip/HelpPopupTooltip";

import { useViewOps } from "./_use-view-ops";
import { viewValidationSchema } from "./_view-form-utils";

export interface ViewFormProps {
	formId: string;
	view: QueryView;
	close: VoidFunction;
}

export const ViewForm: React.FC<ViewFormProps> = ({ formId, view, close }) => {
	const { isLoading, initialValue = {}, handleSubmit } = useViewOps({ view, closeDrawer: close });

	const { data: existingQueryViewsNames = [] } = useQuery({
		...queryQueryNodesList(Type.QueryView),
		select: (queryViews) => queryViews.map((currentView) => currentView.name).filter((name) => name !== view.name),
	});

	return (
		<Formik
			enableReinitialize
			initialValues={initialValue}
			onSubmit={handleSubmit}
			validateOnBlur
			validationSchema={viewValidationSchema}
		>
			{(formik) => (
				<Form
					data-testid="view-edit-form"
					id={formId}
					layout="vertical"
					onSubmitCapture={() => void formik.handleSubmit()}
				>
					<fieldset className="disabled:pointer-events-none disabled:opacity-50" disabled={isLoading}>
						<Input
							data-testid="view-create-modal-view-name"
							label="View name"
							name="name"
							placeholder="Keep it short and sweet"
							required
							validate={(value) =>
								string()
									.required()
									.notOneOf(existingQueryViewsNames, "This view name is already used")
									.validate(value)
									.then(
										() => undefined,
										({ message }: ValidationError) => message,
									)
							}
						/>
						<TextArea
							autoSize={{ minRows: 5, maxRows: 5 }}
							label="Description"
							name="description"
							validate={(value) =>
								string()
									.max(500)
									.validate(value)
									.then(
										() => undefined,
										({ message }: ValidationError) => message,
									)
							}
						/>
						<Input
							data-testid="view-create-modal-identifier"
							label="Identifier"
							labelExtra={
								<HelpPopupTooltip
									content="Identifiers are URNs used for uniquely identifying nodes and edges (relations)."
									moreInfoPlaceholder={
										<HelpPopupTooltipMoreInfoLink capture="noop" slug="ui-popover-identifiers-help">
											Learn more
										</HelpPopupTooltipMoreInfoLink>
									}
								/>
							}
							name="identifier"
							validate={(value) =>
								string()
									.validate(value)
									.then(
										() => undefined,
										({ message }: ValidationError) => message,
									)
							}
						/>
					</fieldset>
				</Form>
			)}
		</Formik>
	);
};
